import { type ButtonHTMLAttributes, forwardRef } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { LoadingSpinner } from "./LoadingSpinner";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[#2447d9] text-white hover:bg-[#1e3a8a] active:bg-[#172554] focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  secondary:
    "bg-white text-[#2447d9] border border-[#2447d9] hover:bg-[#eff3ff] active:bg-[#dbe4ff] focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  ghost:
    "bg-transparent text-[#64748b] hover:bg-[#f1f5f9] active:bg-[#e2e8f0] focus:ring-2 focus:ring-[#e2e8f0] focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
  danger:
    "bg-[#dc2626] text-white hover:bg-[#b91c1c] active:bg-[#991b1b] focus:ring-2 focus:ring-[#dc2626] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "text-xs px-3 py-1.5 rounded-md gap-1.5",
  md: "text-sm px-4 py-2 rounded-lg gap-2",
  lg: "text-base px-6 py-2.5 rounded-lg gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      fullWidth = false,
      className,
      children,
      disabled,
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={twMerge(
          clsx(
            "inline-flex items-center justify-center font-medium transition-colors duration-150 outline-none select-none",
            variantClasses[variant],
            sizeClasses[size],
            fullWidth && "w-full",
            className,
          ),
        )}
        {...rest}
      >
        {loading && (
          <LoadingSpinner
            size="sm"
            className={variant === "primary" || variant === "danger" ? "text-white" : "text-[#2447d9]"}
          />
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
